import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
  IconButton,
  Divider,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import EditIcon from "@mui/icons-material/Edit";
import SchoolIcon from "@mui/icons-material/School";
import PaidIcon from "@mui/icons-material/Paid";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DeleteIcon from "@mui/icons-material/Delete";

// Remaining hours are computed server-side by get_tutor_overview, which
// counts only 'approved' + 'pending' sessions against hours_bought -
// rejected sessions are not consumed hours. The subtraction is only a
// fallback for a payload from an older copy of the RPC (which summed
// usedHours across every status, and so could show a negative balance for an
// assignment that still had hours left).
const remainingHoursFor = (assignment) =>
  Number(
    assignment.remainingHours ??
      Number(assignment.hours_bought || 0) - Number(assignment.usedHours || 0),
  );

export default function TutorAccordion({
  tutor,
  onEditName,
  onCopyEmail,
  onDelete,
  onSetRate,
  onUnassign,
  // Hides every editing control (rename, delete, set rate, unassign) for the
  // read-only viewer role. RLS would refuse the writes anyway.
  readOnly = false,
}) {
  return (
    <Accordion
      sx={{
        mb: 2,
        borderRadius: "12px !important",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={3}
          sx={{ width: "100%" }}
        >
          <PersonIcon color="action" />

          <Box sx={{ width: "35%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {tutor.name}
              </Typography>
              {tutor.maxHourlyRate && (
                <Chip
                  label={`Rate exception: up to R${tutor.maxHourlyRate}/hr`}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              )}
              {!readOnly && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditName(tutor);
                  }}
                  sx={{ p: 1 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Typography
              variant="caption"
              onClick={(e) => {
                e.stopPropagation();
                onCopyEmail(tutor.email);
              }}
              sx={{ cursor: "pointer" }}
            >
              {tutor.email}
            </Typography>
          </Box>

          <Box sx={{ width: "25%" }}>
            <Typography variant="caption" display="block">
              APPROVED HOURS
            </Typography>
            <Typography variant="body2" fontWeight="600">
              {tutor.totalApprovedHours} Hours
            </Typography>
          </Box>

          <Box sx={{ width: "25%" }}>
            <Typography variant="caption" display="block" color="success.main">
              TOTAL EARNINGS
            </Typography>
            <Typography variant="body2" fontWeight="bold" color="success.main">
              R
              {tutor.totalEarnings.toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}
            </Typography>
          </Box>

          {!readOnly && (
            <Stack direction="row" spacing={1} sx={{ ml: "auto", mr: 2 }}>
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(tutor.id);
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          )}
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ bgcolor: "#fafafa" }}>
        <Divider sx={{ mb: 2 }} />
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <SchoolIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2">
            Student Assignments & Rates
          </Typography>
        </Stack>

        {tutor.assignments.length > 0 ? (
          <TableContainer component={Paper} elevation={0} variant="outlined">
            <Table size="small">
              <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell>Remaining Hours</TableCell>
                  <TableCell>Pending Hours</TableCell>
                  <TableCell>Approved Hours</TableCell>
                  <TableCell>Hourly Rate</TableCell>
                  <TableCell>Approved Earnings</TableCell>
                  {!readOnly && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {tutor.assignments.map((assignment) => (
                  <TableRow key={assignment.id}>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      {assignment.students?.full_name}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        color:
                          remainingHoursFor(assignment) < 0
                            ? "error.main"
                            : "primary.main",
                      }}
                    >
                      {remainingHoursFor(assignment).toFixed(2)} hrs
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: "bold", color: "warning.main" }}
                    >
                      {assignment.pendingHours} hrs
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: "bold", color: "success.main" }}
                    >
                      {assignment.approvedHours} hrs
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      R{assignment.rate || 0}/hr
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color="success.main"
                      >
                        R{assignment.totalEarned.toLocaleString()}
                      </Typography>
                    </TableCell>
                    {!readOnly && (
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                        >
                          <Button
                            startIcon={<PaidIcon />}
                            size="small"
                            onClick={() => onSetRate(assignment)}
                          >
                            Set Rate
                          </Button>
                          <Button
                            startIcon={<DeleteIcon />}
                            size="small"
                            color="error"
                            onClick={() => onUnassign(assignment.id)}
                          >
                            Unassign
                          </Button>
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: "italic" }}
          >
            No students currently assigned.
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
